import { DynamicFormCategoryType, DynamicFormQuestionProvider, DynamicQuestion } from '@common/dynamic-form';
import { heraldQuestionId, IndustryAutoCompleteOption } from '@common/types';
import { compact, flatten } from 'lodash';
import { CoverageValuesUpdate, HeraldUpdateApplication, RiskValuesUpdate } from 'clients/types';
import { FormStateBaseProps } from 'broker/pages/SubmissionWorkspacePage/components/DynamicForm/DynamicFormLayout/types';
import { removeEndingAfterUnderscore } from 'broker/pages/SubmissionWorkspacePage/components/DynamicForm/DynamicFormLayout/utils/mapping-utils';

export default function mapFormStateToHerald(
  formState: Record<string, FormStateBaseProps | FormStateBaseProps[] | undefined>,
  heraldNormalizedParameter: DynamicQuestion[],
): Omit<HeraldUpdateApplication, 'products'> {
  const dataToUpdate: Required<Omit<HeraldUpdateApplication, 'products'>> = { coverage_values: [], risk_values: [] };

  const mapStateItemToHeraldParameterUpdate = (
    state: FormStateBaseProps | FormStateBaseProps[],
    normalizedParameter: DynamicQuestion,
  ): CoverageValuesUpdate[] | RiskValuesUpdate[] => {
    const buildHeraldItemForUpdate = (
      formStateItemValue: FormStateBaseProps,
      index?: number,
    ): RiskValuesUpdate | CoverageValuesUpdate => {
      const instance = Array.isArray(state)
        ? `${removeEndingAfterUnderscore(normalizedParameter.instance!)}_${index! + 1}`
        : undefined;
      let normalizedUpdate = formStateItemValue.main;
      if (normalizedUpdate && normalizedParameter.id === heraldQuestionId.rsk_b3jm_2017_naics_index) {
        const industryAutoCompleteValue = normalizedUpdate as IndustryAutoCompleteOption;
        normalizedUpdate = industryAutoCompleteValue.heraldId;
      }
      return {
        [normalizedParameter.category === DynamicFormCategoryType.RiskValues
          ? 'risk_parameter_id'
          : 'coverage_parameter_id']: normalizedParameter.id,
        value: typeof normalizedUpdate !== 'undefined' ? normalizedUpdate : null,
        instance,
        [`child_${normalizedParameter.category}`]: formStateItemValue.children
          ? flatten(
              compact(
                Object.keys(formStateItemValue.children).map((childParameterId) => {
                  const value = formStateItemValue.children![childParameterId];
                  const childNormalizedParameter = normalizedParameter?.arrayElements
                    ?.flatMap((arrayElement) =>
                      arrayElement.childValues?.find((childValue) => childValue.id === childParameterId),
                    )
                    .find(Boolean);

                  if (childNormalizedParameter && value) {
                    return mapStateItemToHeraldParameterUpdate(value, childNormalizedParameter) as RiskValuesUpdate[];
                  }
                  return undefined;
                }),
              ),
            )
          : undefined,
      } as RiskValuesUpdate | CoverageValuesUpdate;
    };

    // isFormArrayField
    if (Array.isArray(state)) {
      const formStateItemValues = state as FormStateBaseProps[];
      return formStateItemValues.map((formStateItemValue, index) =>
        buildHeraldItemForUpdate(formStateItemValue, index),
      ) as RiskValuesUpdate[] | CoverageValuesUpdate[];
    }
    const formStateItemValue = state as FormStateBaseProps;
    return [buildHeraldItemForUpdate(formStateItemValue)] as RiskValuesUpdate[] | CoverageValuesUpdate[];
  };

  Object.keys(formState).forEach((heraldParameterId) => {
    const normalizedParameter = heraldNormalizedParameter.find((param) => param.id === heraldParameterId);
    const formValue = formState[heraldParameterId];
    if (formValue && normalizedParameter?.provider === DynamicFormQuestionProvider.Herald) {
      const valuesForUpdate = mapStateItemToHeraldParameterUpdate(formValue, normalizedParameter);
      if (normalizedParameter.category === DynamicFormCategoryType.RiskValues) {
        dataToUpdate.risk_values = [...dataToUpdate.risk_values, ...(valuesForUpdate as RiskValuesUpdate[])];
      } else {
        dataToUpdate.coverage_values = [
          ...dataToUpdate.coverage_values,
          ...(valuesForUpdate as CoverageValuesUpdate[]),
        ];
      }
    }
  });
  return dataToUpdate;
}
